import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { ArrowRight, CheckCircle, MessageCircle, Phone, Calendar, Quote } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  // Demo images
  const heroSlider = {
    before: "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/u7t538lk_Landscaping%20before.png",
    after: "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/nozb07ha_Landscaping%20after.png"
  };

  const beforeAfterDemos = [
    {
      before: "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/ynzanzdd_Bathroom%20before.png",
      after: "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/plmmqule_Bathroom%20after.png",
      trade: "Bathroom specialist"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/vts3nf9d_Roofing%20before.png",
      after: "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/ukb2gf2l_Roofing%20after.png",
      trade: "Roofer"
    }
  ];

  const testimonials = [
    {
      quote: "Posted my first transformation on Monday. Had 3 enquiries by Wednesday. Two of them booked.",
      name: "James T.",
      trade: "Kitchen fitter, Birmingham"
    },
    {
      quote: "I used to spend ages writing posts that got nothing. Now I just upload my photos and the enquiries come in.",
      name: "Sarah M.",
      trade: "Landscaper, Manchester"
    },
    {
      quote: "Best money I spend each month. The posts actually get people messaging me.",
      name: "Dave R.",
      trade: "Bathroom specialist, Leeds"
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      {/* ==================== HERO SECTION ==================== */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Problem-focused headline */}
            <div className="animate-fade-in-up">
              <p className="text-[#d4af37] font-semibold mb-4 tracking-wide">
                FOR TRADESPEOPLE WHO WANT MORE WORK
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0a2540] tracking-tight leading-tight mb-6">
                Get posts that make people
                <span className="text-[#d4af37]"> message you</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
                Upload 2 photos. Get a post that turns scrollers into enquiries.
              </p>
              <p className="text-slate-500 mb-8 leading-relaxed max-w-lg">
                We turn your before and after photos into scroll-stopping posts with copy 
                that gets homeowners to actually reach out — not just like and move on.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/signup')}
                  className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-14 px-8 text-lg shadow-lg shadow-[#d4af37]/20 active:scale-95 transition-transform"
                  data-testid="hero-cta-btn"
                >
                  Start getting enquiries
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                First post free • No card needed • Takes 2 minutes
              </p>
            </div>

            {/* Right - Demo Slider */}
            <div className="animate-fade-in-up stagger-2">
              <BeforeAfterSlider
                beforeImage={heroSlider.before}
                afterImage={heroSlider.after}
                className="shadow-2xl shadow-slate-300/50"
                aspectRatio="1/1"
              />
              <p className="text-center text-slate-500 text-sm mt-4">
                ← Drag to see the transformation →
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROBLEM AGITATION ==================== */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0a2540] mb-8">
            Sound familiar?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-600 leading-relaxed">
                "I post my job photos on Facebook but nobody ever gets in touch."
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-600 leading-relaxed">
                "I get likes, but likes don't pay the bills."
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-600 leading-relaxed">
                "I don't know what to write — so I just post the photo and hope."
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-600 leading-relaxed">
                "Marketing feels like shouting into the void."
              </p>
            </div>
          </div>
          <p className="text-lg text-[#0a2540] font-medium mt-10">
            You're not bad at social media. <br className="md:hidden" />
            Your posts just aren't built to convert.
          </p>
        </div>
      </section>

      {/* ==================== THE SOLUTION ==================== */}
      <section className="py-16 md:py-20 px-4 bg-[#0a2540]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            What if every post you made was designed to get enquiries?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            SalesAssets turns your job photos into transformation posts with copy 
            that speaks directly to homeowners who are ready to hire.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <MessageCircle className="w-8 h-8 text-[#d4af37] mb-4" />
              <h3 className="text-white font-bold mb-2">Posts that get messages</h3>
              <p className="text-slate-400 text-sm">
                Not just likes — actual enquiries from people who want quotes.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <Phone className="w-8 h-8 text-[#d4af37] mb-4" />
              <h3 className="text-white font-bold mb-2">More calls, less chasing</h3>
              <p className="text-slate-400 text-sm">
                The right words make people pick up the phone. We write them for you.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <Calendar className="w-8 h-8 text-[#d4af37] mb-4" />
              <h3 className="text-white font-bold mb-2">Booked jobs, not busy work</h3>
              <p className="text-slate-400 text-sm">
                Spend 2 minutes posting. Spend the rest of the week doing paid work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a2540] mb-4">
              Here's how it works
            </h2>
            <p className="text-slate-600">
              Three steps. Two minutes. More enquiries.
            </p>
          </div>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-[#0a2540] rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#0a2540] mb-2">
                  Upload your before and after photos
                </h3>
                <p className="text-slate-600">
                  The ones you're already taking. No fancy photography needed.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-[#0a2540] rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#0a2540] mb-2">
                  Tell us what the job was
                </h3>
                <p className="text-slate-600">
                  A quick sentence like "Full bathroom refit in Croydon" — that's all we need.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-[#d4af37] rounded-full flex items-center justify-center text-[#0a2540] font-bold text-xl flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#0a2540] mb-2">
                  Get your post — ready to share
                </h3>
                <p className="text-slate-600">
                  An animated before/after visual plus copy written to make people message you. 
                  Post it to Facebook or Instagram and watch the enquiries come in.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button 
              onClick={() => navigate('/signup')}
              className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-14 px-8 text-lg"
              data-testid="how-it-works-cta"
            >
              Try it free — see for yourself
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ==================== SOCIAL PROOF / TESTIMONIALS ==================== */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a2540] mb-4">
              Tradespeople are getting results
            </h2>
            <p className="text-slate-600">
              Real business owners. Real enquiries. Real booked jobs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-[#f8f9fa] rounded-2xl p-6 relative"
              >
                <Quote className="w-8 h-8 text-[#d4af37]/30 absolute top-4 right-4" />
                <p className="text-[#0a2540] mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-bold text-[#0a2540]">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.trade}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TRANSFORMATION EXAMPLES ==================== */}
      <section className="py-16 md:py-24 px-4 bg-[#f8f9fa]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a2540] mb-4">
              This is what gets enquiries
            </h2>
            <p className="text-slate-600">
              Before and after posts that stop the scroll and start conversations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {beforeAfterDemos.map((demo, index) => (
              <div key={index}>
                <BeforeAfterSlider
                  beforeImage={demo.before}
                  afterImage={demo.after}
                  className="shadow-lg"
                  aspectRatio="1/1"
                />
                <p className="text-center text-slate-500 text-sm mt-3">
                  {demo.trade}
                </p>
              </div>
            ))}
          </div>

          {/* GIF example */}
          <div className="mt-12">
            <div className="bg-[#0a2540] rounded-2xl p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Animated posts get 3x more engagement
                  </h3>
                  <p className="text-slate-400 mb-6">
                    Movement catches the eye. Your transformation becomes a short video 
                    that people actually stop to watch — and then message you about.
                  </p>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d4af37]" />
                      Works on Facebook, Instagram, and Stories
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d4af37]" />
                      Download and post in seconds
                    </li>
                  </ul>
                </div>
                <div className="bg-slate-800 rounded-xl p-3">
                  <img 
                    src="/assets/landscaping_slider_optimized.gif"
                    alt="Animated transformation"
                    className="w-full aspect-square rounded-lg object-cover pointer-events-none"
                    draggable={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0a2540] mb-4">
            One price. Everything included.
          </h2>
          <p className="text-slate-600 mb-12 max-w-xl mx-auto">
            Less than the cost of a Costa coffee each day — and it actually brings in work.
          </p>

          <div className="bg-[#0a2540] rounded-3xl p-8 md:p-10 border-2 border-[#d4af37] max-w-md mx-auto">
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="text-5xl md:text-6xl font-bold text-white">£9</span>
              <span className="text-slate-400 text-lg">/month</span>
            </div>
            <p className="text-slate-400 mb-8">Cancel anytime</p>
            
            <ul className="text-left space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Create engaging posts that generate enquiries</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Animated before/after GIFs</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Copy written to generate enquiries</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Post directly to Facebook & Instagram</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Download for any platform</span>
              </li>
            </ul>

            <Button 
              onClick={() => navigate('/signup')}
              className="w-full bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-14 text-lg"
              data-testid="pricing-cta-btn"
            >
              Start getting enquiries
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-slate-500 text-sm mt-4">
              First post free • No card required to try
            </p>
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-16 md:py-24 px-4 bg-[#0a2540]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Stop posting into the void.
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-xl mx-auto">
            Your next job could come from a post you make today. 
            Upload your photos, get your post, and start seeing real enquiries.
          </p>
          <Button 
            onClick={() => navigate('/signup')}
            className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-14 px-10 text-lg"
            data-testid="final-cta-btn"
          >
            Create your first post free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-slate-500 text-sm mt-4">
            Takes 2 minutes • No credit card needed
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
